/**
 * Widget Configuration Dialog (issue #1258)
 *
 * Shared "Configure Widget" modal, reused by any dashboard widget that
 * exposes a gear-icon configure action via `DashboardWidget`'s `headerAction`
 * slot. Hosts whatever config form fields the widget defines (e.g. title +
 * `WidgetTagFilterEditor` for `MyServicesWidget`) instead of painting them
 * inline inside the widget card, per `homepage-dashboard-spec.md`'s
 * "Configure: Gear icon → modal with config options" guideline.
 *
 * Mirrors the existing `forge-dialog` pattern used by `WidgetCatalogDialog`
 * and `DeleteConfirmationDialog`: an `open`-prop-driven `useEffect` toggles
 * `dialogRef.current.open` rather than conditionally mounting the dialog.
 */

import { useRef, useEffect } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import './WidgetConfigDialog.css';

export interface WidgetConfigDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Dialog heading, e.g. "Configure My Services". */
  title: string;
  /** Called when the dialog is dismissed (Done button, backdrop, or Escape). */
  onClose: () => void;
  /** The widget's config form fields. */
  children: React.ReactNode;
}

const TITLE_ID = 'widget-config-dialog-title';

export function WidgetConfigDialog({
  open,
  title,
  onClose,
  children,
}: WidgetConfigDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
  }, [open]);

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={onClose}
      className="widget-config-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      data-testid="widget-config-dialog"
    >
      <div className="widget-config-dialog__content">
        <h2 id={TITLE_ID} className="forge-typography--headline5">
          {title}
        </h2>

        <div className="widget-config-dialog__body">{children}</div>

        <div className="widget-config-dialog__actions">
          <ForgeButton type="button" variant="text" onClick={onClose}>
            Done
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
